import { DatabaseApiKeyService } from '@qdrant/qdrant-cloud-public-api/cluster/auth/v1/database_api_key';
import { getAuthenticatedUser } from '@qdrant/qdrant-cloud-public-api/iam/v1/connect-query';
import { createFileRoute, lazyRouteComponent, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import { createConnectQueryKey } from '@/connect-query';
import { resolveAccountHasRpcPermissions } from '@/router/resolvers/user';
import { hasClusterRbacEnabled, isClusterHybridCloud } from '@/utils/cluster-utils';

export const Route = createFileRoute(
  '/_authenticated/_dashboard/accounts/$accountId/clusters/$clusterId/_tabs/overview',
)({
  validateSearch: z.object({
    createApiKey: z.coerce.boolean().nullish(),
  }),
  beforeLoad: async ({ context: { queryClient, clusterQuery }, params, search: { createApiKey } }) => {
    if (!createApiKey) {
      return;
    }

    // Drops the flag when the API key dialog it asks for cannot open.
    const stripCreateApiKey = () =>
      redirect({ to: '/accounts/$accountId/clusters/$clusterId/overview', params, replace: true });

    // Cluster version is not required to be checked as having RBAC enabled is enough.
    const { cluster } = await queryClient.ensureQueryData(clusterQuery);
    if (!hasClusterRbacEnabled(cluster) || isClusterHybridCloud(cluster)) {
      throw stripCreateApiKey();
    }

    // When "auto" creating an API key, we want to make sure we check against updated permissions.
    await queryClient.invalidateQueries({ queryKey: createConnectQueryKey({ schema: getAuthenticatedUser }) });

    // Listing declares `read:api_keys` and creating `write:api_keys`, which is the pair the creation dialog needs.
    const canManageApiKeys = await resolveAccountHasRpcPermissions(
      [DatabaseApiKeyService.method.listDatabaseApiKeys, DatabaseApiKeyService.method.createDatabaseApiKey],
      { queryClient, accountId: params.accountId },
    );
    if (!canManageApiKeys) {
      throw stripCreateApiKey();
    }
  },
  loader: ({ context: { queryClient, clusterSummaryMetricsQuery } }) => {
    void queryClient.prefetchQuery(clusterSummaryMetricsQuery);
  },
  component: lazyRouteComponent(() => import('./overview.component'), 'ClusterOverviewComponent'),
});
